import React, { useState, useEffect } from "react";
import {
    Box,
    Button
} from "@mui/material";
import {
    makeGETRequestOnUserService,
    makePOSTRequestOnUserService,
    makePUTRequestOnUserService,
    downloadUserServiceFileUsingGET,
} from "../../Api/api";
import SaveIcon from "@material-ui/icons/Save";
import Skeleton from "@mui/material/Skeleton";
import { Base64 } from "js-base64";
import DrawRoundedIcon from "@mui/icons-material/DrawRounded";
import { saveAs } from "file-saver";

export interface UserDetailsProps {
    currentUser: any;
    user_type: string;
    adminName: string;
    bankDetails?: any;
    dematDetails?: any;
    panCard?: any;
    proofOfAddressDetails?: any;
    activeTab?: any;
}

function checkIfUserCanCompleteKYC(props: UserDetailsProps) {
    let personalDetails = {
        maritalStatus: props.currentUser?.maritalStatus,
        hasPoliticalExposure: props?.currentUser?.hasPoliticalExposure,
        maidenName: props?.currentUser?.maidenName,
        motherName: props?.currentUser?.motherName,
        annualIncome: props?.currentUser?.annualIncome,
        gender: props?.currentUser?.gender,
        occupation: props?.currentUser?.occupation,
    };
    const propertiesToCheck = [
        "bankDetails",
        "dematDetails",
        "panCard",
        "proofOfAddressDetails",
    ];

    for (const property of propertiesToCheck) {
        if (!props[property as keyof UserDetailsProps]) {
            return false;
        }
    }

    return (
        props?.bankDetails &&
        Object.keys(props.bankDetails).length > 0 &&
        props?.panCard &&
        Object.keys(props.panCard).length > 0 &&
        props?.dematDetails &&
        Object.keys(props.dematDetails).length > 0 &&
        props?.proofOfAddressDetails &&
        Object.keys(props.proofOfAddressDetails).length > 0 &&
        personalDetails &&
        Object.keys(personalDetails).length > 0
    );
}

export const CompleteKYCDetails = ({
    currentUser,
    user_type,
    adminName,
    bankDetails,
    dematDetails,
    panCard,
    proofOfAddressDetails,
    activeTab
}: UserDetailsProps) => {
    const toastr = require("toastr");
    const [isLoading, setIsLoading] = useState(false);
    const [openKYCSigning, setOpenKYCSigning] = useState(false);
    const [loading, setLoading] = useState(false);
    const canCompleteKYC = checkIfUserCanCompleteKYC({
        currentUser,
        user_type,
        adminName,
        bankDetails,
        dematDetails,
        panCard,
        proofOfAddressDetails,
    } as UserDetailsProps);
    const [isESignComplete, setIsESignComplete] = useState(
        currentUser?.isESignComplete || false
    );
    const [isRFQRegistrationComplete, setIsRFQRegistrationComplete] = useState(
        currentUser?.isRFQRegistrationComplete || false
    );
    const [areKYCDetailsVerified, setAreKYCDetailsVerified] = useState(
        currentUser?.areKYCDetailsVerified || false
    );
    useEffect(() => {
        if (activeTab === 9) {
            setIsESignComplete(currentUser?.isESignComplete || false);
            setIsRFQRegistrationComplete(
                currentUser?.isRFQRegistrationComplete || false
            );
            setAreKYCDetailsVerified(
                currentUser?.areKYCDetailsVerified || false
            );
        }
    }, [activeTab, currentUser]);

    const options = {
        is_iframe: true,
        environment: "production",
        callback: async function (response: any) {
            if (response?.error_code?.toLowerCase() === "cancelled") {
                toastr.error("Please complete the E-Signing process.");
                setOpenKYCSigning(false);
            } else if (response.hasOwnProperty("error_code")) {
                toastr.error("Something went wrong. Please try again later.");
                setOpenKYCSigning(false);
            } else {
                makePUTRequestOnUserService(`/partneruser/finish//${user_type}/${currentUser.uuid}/`, {})
                    .then((response) => {
                        if (response && response.status === 200) {
                            toastr.success("Your KYC Under Review with SMEST Team");
                            setIsESignComplete(true);
                            setOpenKYCSigning(false);
                        }
                    })
                    .catch((error) => {
                        toastr.error("Something went wrong. Please try again.");
                        setOpenKYCSigning(false);
                    });
            }
        },
        logo: "/src/images/logo.svg",
        theme: {
            primaryColor: "#AB3498",
            secondaryColor: "#000000",
        },
        generate_access_token: true,
    };

    const handleESignDocDownload = async () => {
        try {
            let response = await makeGETRequestOnUserService(
                `/partneruser/kyc/sign_request/create/${currentUser.uuid}/?user_type=${user_type}`
            );
            if (response && response.status === 200) {
                const { id: docID } = response.data;
                let docDownloadResponse = await downloadUserServiceFileUsingGET(
                    `kyc/downloadesigndoc/${docID}/`
                );
                if (docDownloadResponse && docDownloadResponse.status === 200) {
                    toastr.success("E-Sign Document Downloaded Successfully");
                    saveAs(docDownloadResponse.data, `E_Sign_Doc.pdf`);
                }
            }
            setLoading(false);
        } catch (error) {
            toastr.error(error);
            setLoading(false);
        }
    };

    const handleESign = async () => {
        try {
            let response = await makeGETRequestOnUserService(
                `/partneruser/kyc/sign_request/create/${currentUser.uuid}/?user_type=${user_type}`
            );
            if (response && response.status === 200) {
                if (typeof window !== "undefined") {
                    const digio = new window.Digio(options);
                    digio.init();
                    const {
                        id: docID,
                        access_token: { id: tokenID },
                        signing_parties: [{ identifier }],
                    } = response.data;
                    digio.submit(docID, identifier, tokenID);
                    setOpenKYCSigning(false);
                }
            }
        } catch (error) {
            toastr.error(error);
        }
    };

    const markUserKYCAsComplete = async () => {
        try {
            let response = await makePUTRequestOnUserService("/partneruser/mark_complete/", {
                user_id: currentUser.uuid,
                user_type: user_type,
                admin_name: adminName,
            });
            if (response && response.status === 200) {
                toastr.success("User KYC has been marked complete.");
                setAreKYCDetailsVerified(true);
            }
        } catch (error) {
            toastr.error(error);
        }
    };

    const handleUCCRegister = async () => {
        try {
            let response = await makePOSTRequestOnUserService(
                "/order_management_v2/register_ucc/",
                {
                    user_id: currentUser.uuid,
                    admin_name: adminName,
                    user_type: user_type,
                }
            );
            if (response && response.status === 200) {
                toastr.success(response.data.ICDMUCCResponseList[0].message);
                setIsRFQRegistrationComplete(true);
            }
        } catch (error) {
            toastr.error(error);
        }
    };

    const completeUserKYC = async (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.preventDefault();
        await markUserKYCAsComplete();
    };

    function dataURItoBlob(dataURI: string) {
        const byteString = window.atob(dataURI);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const int8Array = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteString.length; i++) {
            int8Array[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([int8Array], { type: "application/pdf" });
        return blob;
    }

    function generateKYCDoc(
        event: React.MouseEvent<HTMLButtonElement>
    ) {
        event.preventDefault();
        if (canCompleteKYC) {
            setIsLoading(true);
            makeGETRequestOnUserService(
                `/digio/doc/generate/${currentUser.uuid}/`
            )
                .then((response) => {
                    setIsLoading(false);
                    if (response && response.status === 200) {
                        let encoded = Base64.encode(response.data);
                        const blob = dataURItoBlob(encoded);
                        const url = URL.createObjectURL(blob);
                        window.open(url, "_blank");
                    }
                })
                .catch((error) => {
                    setIsLoading(false);
                    toastr.error(error);
                });
        } else {
            toastr.warning("One or more required documents missing!");
        }
    }

    if (isLoading) {
        return (
            <Box
                sx={{ width: 900 }}
                style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                }}
            >
                <Skeleton />
                <Skeleton animation="wave" />
                <Skeleton animation={false} />
                <Skeleton animation={false} />
                <Skeleton animation={false} />
                <Skeleton animation={false} />
                <Skeleton animation={false} />
                <Skeleton animation={false} />
            </Box>
        );
    } else {
        if (canCompleteKYC) {
            return (
                <div
                    style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%, -50%)",
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={loading}
                        style={{
                            backgroundColor: loading ? "#d9d9d9" : "#262349",
                            color: loading ? "#9e9e9e" : "#ffffff",
                            marginBottom: "10px",
                        }}
                        startIcon={<SaveIcon />}
                        onClick={() => {
                            setLoading(true);
                            handleESignDocDownload();
                        }}
                    >
                        {loading ? "Downloading, pls wait..." : "Download E-Sign Document"}
                    </Button>
                    <br />
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={
                            isESignComplete || openKYCSigning
                        }
                        style={{
                            backgroundColor: isESignComplete
                                ? "#d9d9d9"
                                : "#262349",
                            color: isESignComplete
                                ? "#9e9e9e"
                                : "#ffffff",
                            marginBottom: "10px",
                        }}
                        startIcon={<DrawRoundedIcon />}
                        onClick={() => {
                            setOpenKYCSigning(true);
                            handleESign();
                        }}
                    >
                        {openKYCSigning ? "Loading Doc, Wait..." : "E-Sign Document"}
                    </Button>
                    <br />
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={isRFQRegistrationComplete}
                        style={{
                            backgroundColor: isRFQRegistrationComplete
                                ? "#d9d9d9"
                                : "#262349",
                            color: isRFQRegistrationComplete
                                ? "#9e9e9e"
                                : "#ffffff",
                            marginBottom: "10px",
                        }}
                        startIcon={<SaveIcon />}
                        onClick={() => handleUCCRegister()}
                    >
                        Register UCC
                    </Button>
                    <br />
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={areKYCDetailsVerified}
                        style={{
                            backgroundColor: areKYCDetailsVerified
                                ? "#d9d9d9"
                                : "#262349",
                            color: areKYCDetailsVerified
                                ? "#9e9e9e"
                                : "#ffffff",
                            marginBottom: "10px",
                        }}
                        startIcon={<SaveIcon />}
                        onClick={completeUserKYC}
                    >
                        Complete KYC
                    </Button>
                </div>
            );
        } else {
            return (
                <div
                    style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%, -50%)",
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        style={{
                            backgroundColor: "#262349",
                            marginBottom: "10px"
                        }}
                        startIcon={<SaveIcon />}
                        onClick={generateKYCDoc}
                    >
                        Generate KYC Doc
                    </Button>
                </div>
            );
        }
    }
};